import { ChevronDown, FileText, Loader2, Pencil } from 'lucide-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    CarePlanSummaryPreview,
    dedupeShiftsByClient,
    readCarePlanFromMetadata,
} from '@apps/web/components/outreaches/care_plan_summary_preview';
import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type JoinedOutreachRead } from '@libraries/typescript/api_client';

type PerShiftCarePlan = {
    readonly summary: string;
    readonly bullets: string;
};

type CarePlanModalButtonProps = {
    readonly outreach: JoinedOutreachRead;
    readonly buttonLabel?: string;
    readonly buttonVariant?: React.ComponentProps<typeof Button>['variant'];
    readonly buttonSize?: React.ComponentProps<typeof Button>['size'];
    readonly className?: string;
    readonly isLoading?: boolean;
    readonly forceVisible?: boolean;
    readonly onSaved?: () => void | Promise<void>;
};

export function CarePlanModalButton({
    outreach,
    buttonLabel = 'Care Plan',
    buttonVariant = 'outline',
    buttonSize = 'sm',
    className,
    isLoading = false,
    forceVisible = false,
    onSaved,
}: CarePlanModalButtonProps) {
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'care-plan-modal',
    });

    const initialCarePlan = useMemo(
        () => readCarePlanFromMetadata(outreach),
        [outreach],
    );

    const [carePlanSummary, setCarePlanSummary] = useState(
        initialCarePlan.carePlanSummary,
    );
    const [carePlanBullets, setCarePlanBullets] = useState(
        initialCarePlan.carePlanBullets,
    );
    const [perShiftCarePlans, setPerShiftCarePlans] = useState<
        Record<string, PerShiftCarePlan>
    >(initialCarePlan.perShiftCarePlans);

    // Seed draft state once per modal open. Avoid clobbering edits when the
    // outreach revalidates in the background while the user is editing.
    const seededOutreachIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!open) {
            seededOutreachIdRef.current = null;
            setIsEditing(false);
            return;
        }
        if (seededOutreachIdRef.current === outreach.id) {
            return;
        }
        seededOutreachIdRef.current = outreach.id;
        setCarePlanSummary(initialCarePlan.carePlanSummary);
        setCarePlanBullets(initialCarePlan.carePlanBullets);
        setPerShiftCarePlans(initialCarePlan.perShiftCarePlans);
    }, [open, outreach.id, initialCarePlan]);

    const isSingleShift = outreach.shifts.length === 1;
    const singleShiftId = isSingleShift ? outreach.shifts[0]?.id : null;

    const handleCarePlanSummaryChange = useCallback(
        (value: string) => {
            setCarePlanSummary(value);
            if (singleShiftId) {
                setPerShiftCarePlans((prev) => ({
                    ...prev,
                    [singleShiftId]: {
                        summary: value,
                        bullets: prev[singleShiftId]?.bullets ?? '',
                    },
                }));
            }
        },
        [singleShiftId],
    );

    const handleCarePlanBulletsChange = useCallback(
        (value: string) => {
            setCarePlanBullets(value);
            if (singleShiftId) {
                setPerShiftCarePlans((prev) => ({
                    ...prev,
                    [singleShiftId]: {
                        summary: prev[singleShiftId]?.summary ?? '',
                        bullets: value,
                    },
                }));
            }
        },
        [singleShiftId],
    );

    const handlePerShiftCarePlanChange = useCallback(
        (shiftId: string, plan: PerShiftCarePlan) => {
            setPerShiftCarePlans((prev) => ({ ...prev, [shiftId]: plan }));
        },
        [],
    );

    const hasSmsStep = outreach.steps.some((step) => step.step_type === 'sms');
    const hasVoiceStep = outreach.steps.some(
        (step) => step.step_type === 'voice',
    );

    const hasAnyCarePlanContent =
        !!initialCarePlan.carePlanSummary.trim() ||
        !!initialCarePlan.carePlanBullets.trim() ||
        Object.values(initialCarePlan.perShiftCarePlans).some(
            (entry) => !!entry.summary.trim() || !!entry.bullets.trim(),
        );
    const hasRawCarePlan = outreach.shifts.some(
        (shift) => !!shift.patient_care_plan?.trim(),
    );
    const hasAnyData = hasAnyCarePlanContent || hasRawCarePlan;

    const handleSave = useCallback(async () => {
        if (!organizationId) {
            return;
        }
        setIsSaving(true);
        try {
            const shiftCarePlans: Record<
                string,
                { summary: string | null; bullets: string | null }
            > = {};
            for (const shift of outreach.shifts) {
                const entry = perShiftCarePlans[shift.id];
                shiftCarePlans[shift.id] = {
                    summary: entry?.summary?.trim() ? entry.summary : null,
                    bullets: entry?.bullets?.trim() ? entry.bullets : null,
                };
            }
            await getApiClient().patch({
                url: '/organizations/{organization_id}/callouts/{callout_id}',
                pathParams: {
                    organization_id: organizationId,
                    callout_id: outreach.id,
                },
                body: {
                    shift_care_plans: shiftCarePlans,
                },
            });
            await onSaved?.();
            toast.success('Care plan updated', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to update care plan:', error);
            toast.error('Failed to update care plan', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
        } finally {
            setIsSaving(false);
        }
    }, [
        onSaved,
        organizationId,
        outreach.id,
        outreach.shifts,
        perShiftCarePlans,
    ]);

    const handleCancel = useCallback(() => {
        setCarePlanSummary(initialCarePlan.carePlanSummary);
        setCarePlanBullets(initialCarePlan.carePlanBullets);
        setPerShiftCarePlans(initialCarePlan.perShiftCarePlans);
        setIsEditing(false);
    }, [initialCarePlan]);

    if (!hasAnyData && !isLoading && !forceVisible) {
        return null;
    }

    return (
        <React.Fragment>
            <Button
                variant={buttonVariant}
                size={buttonSize}
                className={className}
                onClick={() => setOpen(true)}
                disabled={!hasAnyData}
            >
                {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                    <FileText className="mr-2 h-4 w-4" />
                )}
                {buttonLabel}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Care Plans</DialogTitle>
                        <DialogDescription>
                            Care plan summaries (if available) and the raw care
                            plan text.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {hasAnyCarePlanContent && !isEditing && (
                            <div className="flex justify-end">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsEditing(true)}
                                >
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Edit
                                </Button>
                            </div>
                        )}
                        {!hasAnyCarePlanContent && isLoading ? (
                            <section className="text-muted-foreground text-sm">
                                Phoebe is generating the care plan summary…
                            </section>
                        ) : null}
                        <CarePlanSummaryPreview
                            isLoading={isLoading && !hasAnyCarePlanContent}
                            carePlanSummary={carePlanSummary}
                            carePlanBullets={carePlanBullets}
                            perShiftCarePlans={perShiftCarePlans}
                            selectedShifts={outreach.shifts}
                            timezone={organization?.timezone}
                            showSms={hasSmsStep}
                            showVoice={hasVoiceStep}
                            {...(isEditing && {
                                onCarePlanSummaryChange:
                                    handleCarePlanSummaryChange,
                                onCarePlanBulletsChange:
                                    handleCarePlanBulletsChange,
                                onPerShiftCarePlanChange:
                                    handlePerShiftCarePlanChange,
                            })}
                        />
                        {hasRawCarePlan && (
                            <section className="space-y-2">
                                {dedupeShiftsByClient(outreach.shifts).map(
                                    (shift) => {
                                        const rawCarePlan =
                                            shift.patient_care_plan?.trim();
                                        if (!rawCarePlan) {
                                            return null;
                                        }
                                        return (
                                            <Collapsible
                                                key={shift.id}
                                                className="rounded-md border"
                                            >
                                                <CollapsibleTrigger
                                                    asChild={true}
                                                >
                                                    <button
                                                        type="button"
                                                        className="text-muted-foreground flex w-full items-center gap-1 px-3 py-2 text-xs font-medium"
                                                    >
                                                        <ChevronDown className="h-3 w-3" />
                                                        Full care plan —{' '}
                                                        {shift.patient_name}
                                                    </button>
                                                </CollapsibleTrigger>
                                                <CollapsibleContent className="px-3 pb-3">
                                                    <ScrollArea className="h-56 rounded-md border">
                                                        <div className="p-3 text-sm break-words whitespace-pre-wrap">
                                                            {rawCarePlan}
                                                        </div>
                                                    </ScrollArea>
                                                </CollapsibleContent>
                                            </Collapsible>
                                        );
                                    },
                                )}
                            </section>
                        )}
                        {isEditing && (
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : null}
                                    Save
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}
